import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './dark-mode.css';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import reportWebVitals from './reportWebVitals';

import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';

import { initErrorCollector } from './utils/errorCollector';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { notifyControllerChanged, notifyWaiting } from './utils/swUpdate';

initErrorCollector();

// 1) Load OneSignal's Web SDK script dynamically
async function loadOneSignalSDK() {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdn.onesignal.com/sdks/OneSignalPageSDKES6.js';
    script.defer = true;

    // When the script is loaded, check for window.OneSignal
    script.onload = () => {
      if (window.OneSignal) {
        resolve(window.OneSignal);
      } else {
        reject(new Error('OneSignal script loaded, but window.OneSignal is undefined.'));
      }
    };

    script.onerror = (err) => reject(err);
    document.head.appendChild(script);
  });
}

// 2) Initialize OneSignal after loading the script
async function initOneSignal() {
  try {
    const OneSignal = await loadOneSignalSDK();

    await OneSignal.init({
      appId: "ab9a9df9-1ef8-4f97-a8e1-f4053549dd9d",
      allowLocalhostAsSecureOrigin: true,
      notifyButton: { enable: true },
      // OneSignal's worker path and scope come from its DASHBOARD ("typical" setup): init options
      // for them are ignored. Today that is /OneSignalSDKWorker.js at "/", the app worker's scope,
      // so on a browser that allowed notifications the two take turns there; the app worker
      // shows OneSignal pushes too and never reports OneSignal's worker as a new build
      // (serviceWorkerRegistration.js). To give OneSignal its own scope, turn on "Customize
      // service worker paths" in the OneSignal dashboard: path /push/onesignal/, scope
      // /push/onesignal/ (the files are already in public/push/onesignal/).
      // safari_web_id: "web.onesignal.auto...." if you need Safari support
    });

    // Optional: immediately show the slidedown prompt
    // (You can remove this if you'd rather manually prompt the user later)
    OneSignal.showSlidedownPrompt();
    console.log("OneSignal initialized and slidedown prompt shown.");
  } catch (err) {
    console.error("Error loading or initializing OneSignal:", err);
  }
}

// 3) Call initOneSignal in the background (non-blocking). Never on the shop
// wall TV (/tv/…): its slidedown prompt would sit over the board forever. Never on the
// signing page (/sign/…) either: a customer's phone must not be asked for notifications.
if (!window.location.pathname.startsWith("/tv/") && !window.location.pathname.startsWith("/sign/")) {
  initOneSignal();
}

// 4) Render your React App
const root = ReactDOM.createRoot(document.getElementById('root'));
// A render crash anywhere shows a message instead of a blank page.
root.render(<ErrorBoundary label="JobQ"><App /></ErrorBoundary>);

// 5) Register the service worker (src/sw, built by scripts/build-service-worker.js). A new
//    build waits for the "New version, tap to reload" banner (components/UpdateBanner.jsx).
serviceWorkerRegistration.register({
  onUpdate: notifyWaiting,
  onControllerChange: notifyControllerChanged,
});

// 6) Performance tracking (optional)
reportWebVitals();
